import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  HelpCircle,
  Lightbulb,
  MessageSquare,
  Play,
  ShieldAlert,
  Target,
  Terminal,
} from "lucide-react";
import { PagePlaceholder } from "@/components/markyt/PagePlaceholder";
import { ErrorState } from "@/components/shared/error-state";
import { CtaPanel } from "@/components/skills/detail/CtaPanel";
import { DetailTab, DetailTabsList } from "@/components/skills/detail/DetailTabs";
import { InstallBlock } from "@/components/skills/detail/InstallBlock";
import { OwnerTools } from "@/components/skills/detail/OwnerTools";
import { ReviewsSection } from "@/components/skills/detail/ReviewsSection";
import { RiskFindings } from "@/components/skills/detail/RiskFindings";
import { SidePanel } from "@/components/skills/detail/SidePanel";
import { SkillHero } from "@/components/skills/detail/SkillHero";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useSkill } from "@/hooks/use-skill";
import { useSession } from "@/lib/auth-client";
import { canGovern, normalizeRole } from "@/lib/roles";

export function SkillDetailView({ slug }: { slug: string }) {
  const { data: session } = useSession();
  const { data: skill, isLoading, isError, refetch } = useSkill(slug);

  if (isLoading) {
    return (
      <div className="grid gap-6 min-[1100px]:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
        <Skeleton className="h-64 w-full rounded-[12px]" />
      </div>
    );
  }
  if (isError) return <ErrorState onRetry={() => void refetch()} />;
  if (!skill) {
    return (
      <PagePlaceholder
        eyebrow="Skill"
        title="Not found"
        lede="That skill doesn't exist or isn't visible to you."
      />
    );
  }

  const role = normalizeRole(session?.user.role);
  const isAdmin = canGovern(role);
  const isOwner = session?.user.id === skill.ownerId;

  const visibleFindings = skill.findings.filter((f) => f.status !== "dismissed");
  const showRisk = isAdmin && visibleFindings.length > 0;
  const showExamples = skill.examples.length > 0;

  return (
    <div className="grid items-start gap-6 min-[1100px]:grid-cols-[1fr_340px]">
      <div className="flex min-w-0 flex-col gap-6">
        <SkillHero skill={skill} />

        <Tabs defaultValue="overview" className="gap-6">
          <DetailTabsList>
            {showRisk ? (
              <DetailTab
                value="risk"
                icon={ShieldAlert}
                label="Risk scan"
                count={visibleFindings.length}
                alert
              />
            ) : null}
            <DetailTab value="overview" icon={BookOpen} label="Overview" />
            {showExamples ? (
              <DetailTab
                value="examples"
                icon={Lightbulb}
                label="Examples"
                count={skill.examples.length}
              />
            ) : null}
            <DetailTab value="install" icon={Terminal} label="Install" />
            <DetailTab
              value="reviews"
              icon={MessageSquare}
              label="Reviews"
              count={skill.reviews.length}
            />
          </DetailTabsList>

          {showRisk ? (
            <TabsContent value="risk" className="animate-mk-fade">
              <RiskFindings slug={skill.slug} findings={skill.findings} />
            </TabsContent>
          ) : null}

          <TabsContent value="overview" className="flex animate-mk-fade flex-col gap-6">
            <Prose icon={HelpCircle} title="What is it">
              {skill.whatIs}
            </Prose>
            <section className="flex flex-col gap-3">
              <SectionHead icon={Target} title="Why it matters" />
              <div className="rounded-r-[9px] border-l-4 border-l-fg-link bg-bg-2 px-4 py-3 text-fg-2">
                {skill.whyImportant}
              </div>
            </section>
          </TabsContent>

          {showExamples ? (
            <TabsContent value="examples" className="flex animate-mk-fade flex-col gap-3">
              {skill.examples.map((ex) => (
                <div
                  key={ex.scenario}
                  className="rounded-[12px] border border-border-1 bg-white p-4"
                >
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-fg-link">
                    <Play className="size-3.5" />
                    {ex.scenario}
                  </div>
                  <p className="mt-2 text-sm text-fg-2">{ex.body}</p>
                </div>
              ))}
            </TabsContent>
          ) : null}

          <TabsContent value="install" className="animate-mk-fade">
            <InstallBlock
              install={skill.install}
              notes={skill.notes || undefined}
              steps={skill.installSteps}
            />
          </TabsContent>

          <TabsContent value="reviews" className="animate-mk-fade">
            <ReviewsSection slug={skill.slug} reviews={skill.reviews} />
          </TabsContent>
        </Tabs>
      </div>

      <aside className="flex flex-col gap-5 min-[1100px]:sticky min-[1100px]:top-20">
        <CtaPanel skill={skill} />
        <SidePanel skill={skill} />
        <OwnerTools skill={skill} isAdmin={isAdmin} isOwner={isOwner} />
      </aside>
    </div>
  );
}

function SectionHead({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <h2 className="flex items-center gap-2 font-display text-xl font-bold text-fg-1">
      <Icon className="size-5 text-fg-link" />
      {title}
    </h2>
  );
}

function Prose({ icon, title, children }: { icon: LucideIcon; title: string; children: string }) {
  return (
    <section className="flex flex-col gap-3">
      <SectionHead icon={icon} title={title} />
      <p className="max-w-2xl text-fg-2">{children}</p>
    </section>
  );
}
